import { Component, type ReactNode, type ErrorInfo } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Props for the RouteErrorBoundary component
 */
interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Route path for context in error logging */
  routePath?: string;
}

/**
 * State for the RouteErrorBoundary component
 */
interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-specific Error Boundary for isolating errors to individual routes.
 * Provides route-aware error handling with navigation options.
 */
class RouteErrorBoundaryClass extends Component<
  RouteErrorBoundaryProps & { navigate: (path: string) => void },
  RouteErrorBoundaryState
> {
  constructor(
    props: RouteErrorBoundaryProps & { navigate: (path: string) => void }
  ) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `RouteErrorBoundary [${this.props.routePath || "unknown"}]:`,
      error
    );
    console.error("Component stack:", errorInfo.componentStack);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoBack = (): void => {
    window.history.back();
  };

  handleGoHome = (): void => {
    this.props.navigate("/");
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/30 p-6 text-center">
            {/* Error icon */}
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            {/* Error message */}
            <h2 className="text-xl font-semibold text-white mb-2">
              This page encountered an error
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              Something went wrong while loading this content. You can try
              again or navigate to a different page.
            </p>

            {/* Error details (only in development) */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-4 p-3 bg-slate-900/50 rounded-lg text-left overflow-auto max-h-24">
                <p className="text-xs text-amber-300 font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-300 text-sm font-medium rounded-lg hover:bg-cyan-500/30 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              >
                Try Again
              </button>
              <button
                onClick={this.handleGoBack}
                className="px-4 py-2 bg-slate-700/50 text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-700 transition-all focus:outline-none focus:ring-2 focus:ring-slate-500/50"
              >
                Go Back
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 bg-slate-700/50 text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-700 transition-all focus:outline-none focus:ring-2 focus:ring-slate-500/50"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * RouteErrorBoundary wrapper with React Router navigation hook
 */
export function RouteErrorBoundary(props: RouteErrorBoundaryProps): ReactNode {
  const navigate = useNavigate();
  return <RouteErrorBoundaryClass {...props} navigate={navigate} />;
}

export default RouteErrorBoundary;
